import { useEffect, useRef, useState } from "react";

const categories = ["All", "Plotting", "Commercial", "Residential", "Infrastructure"];

const projects = [
  {
    title: "Nex Greens Enclave",
    category: "Plotting",
    location: "Dehradun, UK",
    year: "2024",
    area: "12 Acres",
    desc: "Premium residential plotted development with wide roads, parks, and underground utilities",
    color: "from-red-900 to-blue-800",
    highlight: "120 Plots",
  },
  {
    title: "Business Hub Dehradun",
    category: "Commercial",
    location: "Dehradun, UK",
    year: "2024",
    area: "2.5 Acres",
    desc: "Modern commercial complex with retail spaces, offices, and co-working facilities",
    color: "from-blue-900 to-red-800",
    highlight: "40+ Shops",
  },
  {
    title: "Nex Valley Estate",
    category: "Plotting",
    location: "Dehradun, UK",
    year: "2023",
    area: "8 Acres",
    desc: "Luxury residential plots overlooking the valley with 24/7 security and landscaping",
    color: "from-red-800 to-blue-700",
    highlight: "85 Plots",
  },
  {
    title: "Corporate Tower",
    category: "Commercial",
    location: "Dehradun, UK",
    year: "2024",
    area: "1.8 Acres",
    desc: "Grade-A office space with modern amenities and prime location connectivity",
    color: "from-blue-800 to-red-700",
    highlight: "3 Floors",
  },
  {
    title: "Nex Riverfront Plots",
    category: "Plotting",
    location: "Rishikesh, UK",
    year: "2024",
    area: "15 Acres",
    desc: "Scenic riverfront residential plots with eco-friendly development and green spaces",
    color: "from-red-900 to-blue-600",
    highlight: "180 Plots",
  },
  {
    title: "Retail Arcade",
    category: "Commercial",
    location: "Dehradun, UK",
    year: "2024",
    area: "1.2 Acres",
    desc: "High-street retail destination with branded outlets and entertainment zone",
    color: "from-blue-700 to-red-600",
    highlight: "25 Stores",
  },
];

export default function Projects() {
  const ref = useRef(null);
  const [active, setActive] = useState("All");

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1 }
    );
    if (ref.current) ref.current.querySelectorAll(".animate-on-scroll").forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const filtered = active === "All" ? projects : projects.filter(p => p.category === active);

  return (
    <section id="projects" className="py-28 bg-slate-50" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 animate-on-scroll">
          <div className="inline-flex items-center gap-2 text-red-600 text-xs font-semibold tracking-widest uppercase
            bg-red-50 border border-red-100 px-4 py-2 rounded-full mb-6">
            Our Portfolio
          </div>
          <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 'clamp(2.2rem,5vw,3.5rem)' }}
            className="text-red-950">
            OUR <span className="text-blue-700">PROJECTS</span>
          </h2>
          <p className="text-slate-500 mt-4 max-w-xl mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Showcasing our plotting and commercial developments across the beautiful Doon Valley
          </p>
        </div>

        {/* Filter tabs - Updated to prioritize Plotting & Commercial */}
        <div className="flex flex-wrap gap-3 justify-center mb-12 animate-on-scroll">
          {categories.map(c => (
            <button key={c} onClick={() => setActive(c)}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                active === c
                  ? "bg-gradient-to-r from-red-900 to-blue-900 text-white shadow-lg shadow-red-200"
                  : "bg-white text-red-700 border border-red-200 hover:border-red-400"
              }`}
              style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {c}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p, i) => (
            <div key={p.title}
              className="animate-on-scroll group relative overflow-hidden rounded-2xl cursor-pointer"
              style={{ transitionDelay: `${i * 80}ms` }}>
              {/* Visual background - Updated with red-blue gradients */}
              <div className={`bg-gradient-to-br ${p.color} h-52 relative overflow-hidden`}>
                {/* Geometric decoration */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute bottom-0 left-0 right-0 h-32"
                    style={{ background: "repeating-linear-gradient(90deg, white 0px, white 1px, transparent 1px, transparent 20px)" }}/>
                  <div className="absolute bottom-0 left-0 right-0 h-24"
                    style={{ background: "repeating-linear-gradient(0deg, white 0px, white 1px, transparent 1px, transparent 20px)" }}/>
                </div>

                {/* Plot/Building silhouette based on category */}
                <div className="absolute bottom-0 left-0 right-0 flex items-end justify-center gap-1 px-6">
                  {p.category === "Plotting" ? (
                    // Plot layout pattern
                    Array(8).fill(0).map((_, j) => (
                      <div key={j} className="flex-1 flex flex-col items-center">
                        <div className="w-full border border-white/30 h-16 rounded-sm bg-white/5" />
                        <div className="w-4 h-4 bg-white/20 rounded-sm mt-1" />
                      </div>
                    ))
                  ) : (
                    // Commercial building pattern
                    [50, 80, 60, 100, 70, 45].map((h, j) => (
                      <div key={j} className="flex-1 bg-white/15 rounded-t-sm" style={{ height: h }} />
                    ))
                  )}
                </div>

                {/* Highlight badge */}
                <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white text-xs
                  font-bold tracking-wider px-3 py-1.5 rounded-full border border-white/20">
                  {p.highlight}
                </div>

                {/* Category */}
                <div className="absolute top-4 left-4 bg-red-500/30 text-white text-xs font-semibold
                  tracking-wider px-3 py-1.5 rounded-full">
                  {p.category}
                </div>
              </div>

              {/* Content */}
              <div className="bg-white p-6 border border-red-50 group-hover:border-red-200
                transition-colors duration-300 rounded-b-2xl">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-red-950 font-bold text-lg"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.02em' }}>
                    {p.title}
                  </h3>
                  <span className="text-blue-600 text-sm">{p.year}</span>
                </div>

                <p className="text-slate-500 text-sm mb-4 leading-relaxed">{p.desc}</p>

                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    {p.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
                    </svg>
                    {p.area}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12 animate-on-scroll">
          <a href="#contact"
            className="inline-flex items-center gap-2 border-2 border-red-600 text-red-600
            hover:bg-gradient-to-r hover:from-red-900 hover:to-blue-900 hover:text-white hover:border-transparent
            px-8 py-3 rounded-xl font-semibold transition-all duration-300">
            Discuss Your Land Requirements
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}